import { useMemo } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmPlanChangeDialog } from '@/components/customer/billing/ConfirmPlanChangeDialog';
import type { CustomerPlan } from '@/types/customer-plan-management';
import { resolveBillingCycleLabel } from '@/lib/billing-cycle-label';

interface PlanCardProps {
  plan: CustomerPlan;
  preferredCycleId?: string | null;
  currentSelectionOverride?: boolean;
  isLoading?: boolean;
  actionsDisabled?: boolean;
  actionsDisabledReason?: string;
}

type PlanCardManageProps = PlanCardProps & {
  mode?: 'manage';
  isUpgrade: boolean;
  canDowngrade: boolean;
  hasDirectAction: boolean;
  onUpgrade: () => void;
  onDowngrade: () => void;
};

type PlanCardSelectProps = PlanCardProps & {
  mode: 'select';
  onSelect: (selection: { planId: string; priceId: string | null; cycleId: string | null }) => void;
};

export function PlanCard(props: PlanCardManageProps | PlanCardSelectProps) {
  const {
    plan,
    preferredCycleId = null,
    currentSelectionOverride,
    isLoading = false,
    actionsDisabled = false,
    actionsDisabledReason,
  } = props;
  const { t, i18n } = useTranslation();

  const isManageMode = props.mode !== 'select';
  const isUpgrade = isManageMode ? props.isUpgrade : false;
  const canDowngrade = isManageMode ? props.canDowngrade : false;
  const hasDirectAction = isManageMode ? props.hasDirectAction : false;

  const candidatePrices = useMemo(() => {
    const candidates = plan.pricing.filter((price) => Boolean(price.formattedPrice || price.price !== null));
    return candidates;
  }, [plan.pricing]);

  const primaryPrice = useMemo(() => {
    if (!candidatePrices.length) return null;

    if (preferredCycleId) {
      const match = candidatePrices.find((price) => price.cycleId === preferredCycleId);
      if (match) return match;
    }

    return candidatePrices[0] ?? null;
  }, [candidatePrices, preferredCycleId]);

  const secondaryPrices = useMemo(
    () => (primaryPrice ? candidatePrices.filter((price) => price.id !== primaryPrice.id) : []),
    [candidatePrices, primaryPrice],
  );

  const primaryIntervalLabel = useMemo(() => {
    if (!primaryPrice) return null;
    return resolveBillingCycleLabel({ cycleName: primaryPrice.cycleName, interval: primaryPrice.interval }, t);
  }, [primaryPrice, t]);

  const formatPrice = (price: (typeof candidatePrices)[number] | null): string | null => {
    if (!price) return null;
    if (price.formattedPrice) return price.formattedPrice;

    if (typeof price.price === 'number') {
      const currency = price.currency ?? 'USD';
      try {
        return new Intl.NumberFormat(i18n.language ?? 'en', {
          style: 'currency',
          currency,
        }).format(price.price / 100);
      } catch {
        return `${currency} ${(price.price / 100).toFixed(2)}`;
      }
    }

    return null;
  };

  const resolvedPrice = useMemo(() => {
    return formatPrice(primaryPrice);
  }, [i18n.language, primaryPrice]);

  const secondaryPriceLines = useMemo(() => {
    return secondaryPrices
      .map((price) => {
        const label = resolveBillingCycleLabel({ cycleName: price.cycleName, interval: price.interval }, t);
        const amount = formatPrice(price);
        if (!label || !amount) return null;
        return { id: price.id, label, amount };
      })
      .filter((line): line is { id: string; label: string; amount: string } => Boolean(line));
  }, [i18n.language, secondaryPrices, t]);
  const isCurrentSelection =
    typeof currentSelectionOverride === 'boolean'
      ? currentSelectionOverride
      : Boolean(primaryPrice?.isCurrentSelection);

  return (
    <Card className={isCurrentSelection ? 'border-primary shadow-sm' : 'transition-colors hover:border-primary/40'}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle>{plan.name}</CardTitle>
            {plan.description ? <CardDescription>{plan.description}</CardDescription> : null}
          </div>
          <div className="flex items-center gap-2">
            {plan.tier ? <Badge variant="secondary">{plan.tier}</Badge> : null}
            {isCurrentSelection ? <Badge>{t('customer.planManagement.plans.current')}</Badge> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-2xl font-semibold">
            {resolvedPrice ?? t('customer.planManagement.plans.customPricing')}
          </p>
          {primaryIntervalLabel ? (
            <p className="text-xs text-muted-foreground">{primaryIntervalLabel}</p>
          ) : null}
          {secondaryPriceLines.length ? (
            <div className="mt-2 space-y-1">
              {secondaryPriceLines.map((line) => (
                <p key={line.id} className="text-xs text-muted-foreground">
                  {line.amount} · {line.label}
                </p>
              ))}
            </div>
          ) : null}
        </div>

        {plan.maxSeats ? (
          <p className="text-sm text-muted-foreground">
            {t('customer.planManagement.plans.seatLimit', { count: plan.maxSeats })}
          </p>
        ) : null}

        {plan.features.length ? (
          <div>
            <ul className="space-y-2 text-sm">
              {plan.features.slice(0, 5).map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 text-emerald-600" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            {plan.features.length > 5 ? (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('customer.planManagement.plans.moreFeatures', { count: plan.features.length - 5 })}
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
      <CardFooter>
        {isCurrentSelection ? (
          <Button variant="outline" disabled className="w-full">
            {t('customer.planManagement.plans.currentPlan')}
          </Button>
        ) : props.mode === 'select' ? (
          <Button
            className="w-full"
            onClick={() =>
              props.onSelect({
                planId: plan.id,
                priceId: primaryPrice?.id ?? null,
                cycleId: primaryPrice?.cycleId ?? null,
              })
            }
            disabled={!primaryPrice?.id || isLoading}
          >
            {t('customer.planManagement.actions.choosePlan')}
          </Button>
        ) : isManageMode && isUpgrade ? (
          actionsDisabled ? (
            <Button className="w-full" disabled title={actionsDisabledReason}>
              {t('customer.planManagement.actions.upgrade')}
            </Button>
          ) : (
            <ConfirmPlanChangeDialog
              type="upgrade"
              planName={plan.name}
              onConfirm={props.onUpgrade}
              isLoading={isLoading}
            >
              <Button className="w-full">{t('customer.planManagement.actions.upgrade')}</Button>
            </ConfirmPlanChangeDialog>
          )
        ) : isManageMode && canDowngrade ? (
          actionsDisabled ? (
            <Button variant="secondary" className="w-full" disabled title={actionsDisabledReason}>
              {t('customer.planManagement.actions.downgrade')}
            </Button>
          ) : (
            <ConfirmPlanChangeDialog
              type="downgrade"
              planName={plan.name}
              onConfirm={props.onDowngrade}
              isLoading={isLoading}
            >
              <Button variant="secondary" className="w-full">
                {t('customer.planManagement.actions.downgrade')}
              </Button>
            </ConfirmPlanChangeDialog>
          )
        ) : isManageMode && hasDirectAction ? (
          <Button className="w-full" variant="outline" disabled>
            {t('customer.planManagement.actions.unavailable')}
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  );
}
